import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { api, ApiError } from "../api/client";
import type { AuthSession } from "../api/types";

interface AuthGateProps {
  children: (session: AuthSession, logout: () => Promise<void>, loggingOut: boolean) => ReactNode;
}

function messageFor(error: unknown) {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "로그인 상태를 확인하지 못했습니다.";
}

function AuthLoading() {
  return (
    <main className="auth-page" aria-label="로그인 상태 확인 중">
      <section className="auth-card auth-card--loading">
        <div className="auth-brand-mark">S</div>
        <span className="skeleton auth-skeleton auth-skeleton--title" />
        <span className="skeleton auth-skeleton auth-skeleton--copy" />
        <span className="skeleton auth-skeleton auth-skeleton--button" />
      </section>
    </main>
  );
}

interface GoogleLoginProps {
  clientId?: string;
  busy: boolean;
  error?: string;
  onCredential: (credential: string) => void;
  onRetrySession: () => void;
}

const REMEMBERED_MANAGER_NAME = "chongmu.rememberedManagerName";

interface NameLoginProps {
  deviceTrusted: boolean;
  busy: boolean;
  error?: string;
  onLogin: (name: string, accessCode: string | undefined, remember: boolean) => void;
  onRetrySession: () => void;
}

function rememberedManagerName() {
  try { return window.localStorage.getItem(REMEMBERED_MANAGER_NAME) ?? ""; } catch { return ""; }
}

function NameLogin({ deviceTrusted, busy, error, onLogin, onRetrySession }: NameLoginProps) {
  const remembered = rememberedManagerName();
  const [name, setName] = useState(remembered);
  const [accessCode, setAccessCode] = useState("");
  const [remember, setRemember] = useState(Boolean(remembered));

  function submit(event: FormEvent) {
    event.preventDefault();
    const normalized = name.trim();
    if (!normalized || (!deviceTrusted && !accessCode)) return;
    onLogin(normalized, deviceTrusted ? undefined : accessCode, remember);
  }

  return (
    <main className="auth-page">
      <form className="auth-card" onSubmit={submit}>
        <div className="auth-brand-mark">S</div>
        <p className="eyebrow">PROJECT SELMO</p>
        <h1>이름으로 접속</h1>
        <p className="auth-card__copy">담당자 명단에 등록된 이름을 입력하면 바로 업무를 시작할 수 있어요.</p>
        {error && <div className="auth-error" role="alert">{error}</div>}
        <div className="auth-fields">
          <label className="field auth-field"><span>이름</span><input autoFocus required autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} placeholder="예: 김수빈" /></label>
          {!deviceTrusted && <label className="field auth-field"><span>회사 접속코드</span><input required type="password" autoComplete="one-time-code" value={accessCode} onChange={(event) => setAccessCode(event.target.value)} placeholder="이 기기에서 처음 한 번만 입력" /></label>}
          <label className="auth-remember"><input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} /><span>이 기기에서 이름 기억하기</span></label>
        </div>
        <button className="button button--primary auth-submit" type="submit" disabled={busy || !name.trim() || (!deviceTrusted && !accessCode)}>{busy ? "접속 확인 중…" : "접속하기"}</button>
        {busy && <div className="auth-progress"><i className="spinner" /> 담당자 정보를 확인하고 있어요.</div>}
        {error && !busy && <button type="button" className="button button--secondary auth-retry" onClick={onRetrySession}>다시 확인</button>}
        <small className="auth-card__notice">이름은 현재 브라우저에만 저장됩니다. 회사 접속코드는 승인된 기기 확인에만 사용되며 업무 기록에는 남지 않습니다.</small>
      </form>
    </main>
  );
}

function GoogleLogin({ clientId, busy, error, onCredential, onRetrySession }: GoogleLoginProps) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [scriptError, setScriptError] = useState<string>();

  useEffect(() => {
    if (!clientId || busy) return;
    let active = true;

    const render = () => {
      if (!active || !window.google || !buttonRef.current) return;
      buttonRef.current.replaceChildren();
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => {
          if (response.credential) onCredential(response.credential);
        },
        auto_select: false,
        cancel_on_tap_outside: true,
        use_fedcm_for_prompt: true,
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "continue_with",
        shape: "rectangular",
        width: Math.min(320, buttonRef.current.clientWidth || 320),
        locale: "ko",
      });
      setScriptError(undefined);
    };

    if (window.google) {
      render();
      return () => { active = false; };
    }

    const scriptId = "google-identity-services";
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    const handleError = () => active && setScriptError("Google 로그인 화면을 불러오지 못했습니다.");
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener("load", render);
    script.addEventListener("error", handleError);
    return () => {
      active = false;
      script?.removeEventListener("load", render);
      script?.removeEventListener("error", handleError);
    };
  }, [busy, clientId, onCredential]);

  const visibleError = error || scriptError || (!clientId ? "Google Client ID가 설정되지 않았습니다." : undefined);

  return (
    <main className="auth-page">
      <section className="auth-card">
        <div className="auth-brand-mark">S</div>
        <p className="eyebrow">PROJECT SELMO</p>
        <h1>프로젝트 셀모 업무 관리</h1>
        <p className="auth-card__copy">승인된 팀 계정으로 로그인하면 프로젝트와 정산 내역을 안전하게 관리할 수 있어요.</p>
        {visibleError && <div className="auth-error" role="alert">{visibleError}</div>}
        <div className={`google-signin ${busy ? "is-busy" : ""}`} ref={buttonRef} aria-busy={busy} />
        {busy && <div className="auth-progress"><i className="spinner" /> 로그인 확인 중…</div>}
        {(visibleError || !clientId) && !busy && (
          <button className="button button--secondary auth-retry" onClick={onRetrySession}>다시 확인</button>
        )}
        <small className="auth-card__notice">로그인 정보는 서버 세션에만 보관되며 Google 비밀번호는 저장하지 않습니다.</small>
      </section>
    </main>
  );
}

export function AuthGate({ children }: AuthGateProps) {
  const [session, setSession] = useState<AuthSession>();
  const [checking, setChecking] = useState(true);
  const [loginBusy, setLoginBusy] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [error, setError] = useState<string>();

  const checkSession = useCallback(async (signal?: AbortSignal) => {
    setChecking(true);
    setError(undefined);
    try {
      setSession(await api.authSession(signal));
    } catch (sessionError) {
      if (sessionError instanceof DOMException && sessionError.name === "AbortError") return;
      setError(messageFor(sessionError));
    } finally {
      if (!signal?.aborted) setChecking(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void checkSession(controller.signal);
    return () => controller.abort();
  }, [checkSession]);

  const login = useCallback(async (credential: string) => {
    setLoginBusy(true);
    setError(undefined);
    try {
      await api.googleLogin(credential);
      const next = await api.authSession();
      if (!next.authenticated) throw new ApiError("이 계정은 접근 권한이 없습니다.", 403);
      setSession(next);
    } catch (loginError) {
      setError(messageFor(loginError));
    } finally {
      setLoginBusy(false);
    }
  }, []);

  const loginByName = useCallback(async (name: string, accessCode: string | undefined, remember: boolean) => {
    setLoginBusy(true);
    setError(undefined);
    try {
      await api.nameLogin(name, accessCode);
      const next = await api.authSession();
      if (!next.authenticated) throw new ApiError("담당자 접속을 완료하지 못했습니다.", 403);
      try {
        if (remember) window.localStorage.setItem(REMEMBERED_MANAGER_NAME, name);
        else window.localStorage.removeItem(REMEMBERED_MANAGER_NAME);
      } catch { /* Browser privacy settings may disable storage; login still succeeds. */ }
      setSession(next);
    } catch (loginError) {
      setError(messageFor(loginError));
    } finally {
      setLoginBusy(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setLoggingOut(true);
    try {
      await api.logout();
      if (session?.mode === "google") window.google?.accounts.id.disableAutoSelect();
      const next = await api.authSession();
      setSession(next);
    } catch (logoutError) {
      setError(messageFor(logoutError));
    } finally {
      setLoggingOut(false);
    }
  }, [session?.mode]);

  if (checking && !session) return <AuthLoading />;

  if (!session?.authenticated) {
    if (session?.mode === "name") {
      return (
        <NameLogin
          deviceTrusted={Boolean(session.deviceTrusted)}
          busy={loginBusy}
          error={error}
          onLogin={loginByName}
          onRetrySession={() => void checkSession()}
        />
      );
    }
    return (
      <GoogleLogin
        clientId={session?.googleClientId}
        busy={loginBusy}
        error={error}
        onCredential={login}
        onRetrySession={() => void checkSession()}
      />
    );
  }

  return <>{children(session, logout, loggingOut)}</>;
}
